import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import {
  BLOG_CATEGORY_FILTERS,
  BlogCategoryId,
  BlogListPagination,
  BlogPostDto,
  BlogPostPayload,
  Notification,
} from '../types';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { isPlatformSystemAdmin } from '../utils/roleUtils';
import { cssUrl, resolveBlogFeaturedImageSrc } from '../utils/blogImage';
import './BlogAdminPage.design.css';

type StatusFilterId = 'all' | 'published' | 'draft' | 'scheduled' | 'archived';
type SortModeId = 'updated-desc' | 'published-desc' | 'title-asc' | 'status-asc';
type StatusKey = 'published' | 'draft' | 'scheduled' | 'archived';

const STATUS_FILTERS: Array<{ id: StatusFilterId; label: string }> = [
  { id: 'all', label: 'All statuses' },
  { id: 'published', label: 'Published' },
  { id: 'draft', label: 'Draft' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'archived', label: 'Archived' },
];

const SORT_OPTIONS: Array<{ id: SortModeId; label: string }> = [
  { id: 'updated-desc', label: 'Last updated' },
  { id: 'published-desc', label: 'Publish date' },
  { id: 'title-asc', label: 'Title A–Z' },
  { id: 'status-asc', label: 'Status' },
];

const PAGE_SIZE_OPTIONS = [5, 10] as const;

const CATEGORY_LABELS: Record<string, string> = {
  'getting-started': 'Getting Started',
  instructions: 'Instructions',
  'release-notes': 'Release Notes',
  compliance: 'Compliance',
  'founder-updates': 'Founder Updates',
};

const CATEGORY_IMAGE_CLASS: Record<string, string> = {
  'getting-started': 'image-start',
  instructions: 'image-setup',
  'release-notes': 'image-updates',
  compliance: 'image-payments',
  'founder-updates': 'image-customer',
};

const STATUS_LABELS: Record<StatusKey, string> = {
  published: 'Published',
  draft: 'Draft',
  scheduled: 'Scheduled',
  archived: 'Archived',
};

function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] || category;
}

function categoryImageClass(category: string): string {
  return CATEGORY_IMAGE_CLASS[category] || 'image-start';
}

function postImageUrl(post: BlogPostDto): string | null {
  return resolveBlogFeaturedImageSrc(post);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

/** Prefer explicit status unless the caller is filtering for archived internal-visibility posts. */
function statusInfo(post: BlogPostDto, activeStatusFilter: StatusFilterId): { key: StatusKey; label: string } {
  if (activeStatusFilter === 'archived' && post.visibility === 'internal') {
    return { key: 'archived', label: STATUS_LABELS.archived };
  }
  const raw = String(post.status || '').toUpperCase();
  if (raw === 'PUBLISHED') return { key: 'published', label: STATUS_LABELS.published };
  if (raw === 'READY_FOR_REVIEW') return { key: 'scheduled', label: STATUS_LABELS.scheduled };
  return { key: 'draft', label: STATUS_LABELS.draft };
}

function buildPageNumbers(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set<number>([1, 2, total - 1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const result: Array<number | 'ellipsis'> = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) result.push('ellipsis');
    result.push(p);
    prev = p;
  }
  return result;
}

const PlusIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeLinecap="round" strokeWidth="1.9" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="m6 9 6 6 6-6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const FolderIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M4 6h6l2 2h8v10H4V6Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const EditIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 15.5V20Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const EyeIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const CopyIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <rect x="8" y="8" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4H5.5A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const TrashIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M5 7h14M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7m2 0-.7 12.1a2 2 0 0 1-2 1.9H9.7a2 2 0 0 1-2-1.9L7 7"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
  </svg>
);

const KebabIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <circle cx="12" cy="5.5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="18.5" r="1.6" fill="currentColor" />
  </svg>
);

const CloseIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeLinecap="round" strokeWidth="1.9" />
  </svg>
);

const SearchIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z" stroke="currentColor" strokeWidth="1.8" />
    <path d="m16.2 16.2 3.8 3.8" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
  </svg>
);

const FilterIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="m8.2 12.3 2.4 2.4 5.2-5.4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
  </svg>
);

const DraftIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M7 4h10a2 2 0 0 1 2 2v14l-7-3-7 3V6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const ClockIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7.5V12l3 2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
  </svg>
);

const TagIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M4 6h6l10 10-6 6L4 12V6Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="8" cy="10" r="1.4" fill="currentColor" />
  </svg>
);

function CategoryIcon({ category }: { category: string }) {
  switch (category) {
    case 'getting-started':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M5 19V5l14 7-14 7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
    case 'instructions':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M7 4h10a2 2 0 0 1 2 2v14l-7-3-7 3V6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M9 9h6M9 13h4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        </svg>
      );
    case 'release-notes':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M12 3v3M12 18v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M3 12h3M18 12h3M4.9 19.1 7 17M17 7l2.1-2.1" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
          <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'compliance':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M12 3 5 6v6c0 4.2 2.9 7.3 7 8.5 4.1-1.2 7-4.3 7-8.5V6l-7-3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="m9.2 12 1.9 1.9 3.7-3.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
        </svg>
      );
    case 'founder-updates':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
          <path d="M5.5 19.5c1.6-3 3.8-4.5 6.5-4.5s4.9 1.5 6.5 4.5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        </svg>
      );
    default:
      return <FolderIcon />;
  }
}

const BlogAdminPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);

  const [posts, setPosts] = useState<BlogPostDto[]>([]);
  const [pagination, setPagination] = useState<BlogListPagination>({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [topicCount, setTopicCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [statusFilter, setStatusFilter] = useState<StatusFilterId>('all');
  const [categoryFilter, setCategoryFilter] = useState<BlogCategoryId | 'all'>('all');
  const [sortMode, setSortMode] = useState<SortModeId>('updated-desc');

  const [openMenuSlug, setOpenMenuSlug] = useState<string | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);

  const [previewPost, setPreviewPost] = useState<BlogPostDto | null>(null);
  const [previewBodyHtml, setPreviewBodyHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const previewDialogRef = useRef<HTMLDialogElement>(null);
  const filterTupleRef = useRef({ q: debouncedQ, cat: categoryFilter, status: statusFilter, sort: sortMode, limit: pageSize });

  const canManage = Boolean(user && isPlatformSystemAdmin(user));

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        setUnreadNotifications(notifications.filter((n: Notification) => !n.is_read));
      } catch {
        setUnreadNotifications([]);
      }
    };
    void loadNotifications();
  }, [user?.id]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(searchQuery.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  const loadPosts = useCallback(
    async (pageOverride?: number) => {
      if (!canManage) return;
      try {
        setLoading(true);
        const result = await apiService.getAdminBlogPosts({
          category: categoryFilter === 'all' ? undefined : categoryFilter,
          q: debouncedQ || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          sort: sortMode,
          page: pageOverride ?? page,
          limit: pageSize,
        });
        setPosts(result.posts);
        setPagination(result.pagination);
        setStatusCounts(result.statusCounts);
        setTopicCount(result.topicCount);
        setLoadError(null);
      } catch (e: unknown) {
        setLoadError(e instanceof Error ? e.message : 'Failed to load blog posts');
        setPosts([]);
        setPagination((p) => ({ ...p, total: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false }));
      } finally {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    },
    [canManage, categoryFilter, debouncedQ, page, pageSize, sortMode, statusFilter]
  );

  useEffect(() => {
    if (!canManage) return;
    const prev = filterTupleRef.current;
    const tupleChanged =
      prev.q !== debouncedQ ||
      prev.cat !== categoryFilter ||
      prev.status !== statusFilter ||
      prev.sort !== sortMode ||
      prev.limit !== pageSize;
    if (tupleChanged) {
      filterTupleRef.current = { q: debouncedQ, cat: categoryFilter, status: statusFilter, sort: sortMode, limit: pageSize };
    }
    const pageForRequest = tupleChanged ? 1 : page;
    if (tupleChanged && page !== 1) {
      setPage(1);
      return;
    }
    void loadPosts(pageForRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, page, debouncedQ, categoryFilter, statusFilter, sortMode, pageSize]);

  useEffect(() => {
    if (!openMenuSlug) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('.card-actions')) setOpenMenuSlug(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenuSlug(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenuSlug]);

  useEffect(() => {
    const dialog = previewDialogRef.current;
    if (!dialog) return;
    const handleClose = () => {
      setPreviewPost(null);
      setPreviewBodyHtml('');
      setPreviewError(null);
      setPreviewLoading(false);
    };
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, []);

  const rangeStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const rangeEnd = Math.min(pagination.page * pagination.limit, pagination.total);
  const pageNumbers = useMemo(
    () => buildPageNumbers(pagination.page || 1, pagination.totalPages || 0),
    [pagination.page, pagination.totalPages]
  );

  const openPreview = async (post: BlogPostDto) => {
    setOpenMenuSlug(null);
    setPreviewPost(post);
    setPreviewBodyHtml(post.bodyHtml ?? '');
    setPreviewError(null);
    setPreviewLoading(true);
    previewDialogRef.current?.showModal();
    try {
      const full = await apiService.getAdminBlogPostBySlug(post.slug);
      setPreviewPost(full);
      setPreviewBodyHtml(full.bodyHtml ?? '');
    } catch {
      setPreviewError('Could not load the full article preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    previewDialogRef.current?.close();
  };

  const handleDuplicate = async (post: BlogPostDto) => {
    setOpenMenuSlug(null);
    try {
      setBusySlug(post.slug);
      const full = await apiService.getAdminBlogPostBySlug(post.slug);
      const payload: BlogPostPayload = {
        title: `${full.title} (Copy)`,
        category: (full.category as BlogCategoryId) || 'getting-started',
        badge: full.badge,
        summary: full.summary,
        bodyHtml: full.bodyHtml ?? '',
        tags: full.tags || [],
        featuredImageUrl: full.featuredImageUrl,
        thumbIconClass: full.thumbIconClass,
        authorDisplayName: full.authorDisplayName,
        status: 'DRAFT',
        visibility: (full.visibility as 'public' | 'unlisted' | 'internal') || 'public',
        featured: false,
        seoTitle: full.seoTitle,
        seoDescription: full.seoDescription,
        slug: undefined,
        publishedAt: null,
      };
      const created = await apiService.createBlogPost(payload);
      toast.success('Article duplicated as a new draft');
      await loadPosts();
      navigate(`/blog/edit/${created.slug}`);
    } catch {
      toast.error('Could not duplicate this article');
    } finally {
      setBusySlug(null);
    }
  };

  const handleDelete = async (post: BlogPostDto) => {
    setOpenMenuSlug(null);
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      setBusySlug(post.slug);
      await apiService.deleteBlogPost(post.id);
      toast.success('Article deleted');
      await loadPosts();
    } catch {
      toast.error('Could not delete this article');
    } finally {
      setBusySlug(null);
    }
  };

  if (user && !isPlatformSystemAdmin(user)) {
    return <Navigate to="/dashboard/customer" replace />;
  }

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f8fc]">
        <CustomerNavbar
          onLogout={handleLogout}
          showDashboardLink
          showLogoutButton
          showAuthButtons
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <div className="flex-1 flex items-center justify-center py-24">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  const renderSummaryCards = () => (
    <div className="summary-row">
      <div className="summary-card">
        <span className="summary-icon">
          <CheckCircleIcon />
        </span>
        <div>
          <div className="summary-number">{statusCounts.published ?? 0}</div>
          <div className="summary-label">Published</div>
        </div>
      </div>
      <div className="summary-card">
        <span className="summary-icon">
          <DraftIcon />
        </span>
        <div>
          <div className="summary-number">{statusCounts.draft ?? 0}</div>
          <div className="summary-label">Drafts</div>
        </div>
      </div>
      <div className="summary-card">
        <span className="summary-icon">
          <ClockIcon />
        </span>
        <div>
          <div className="summary-number">{statusCounts.scheduled ?? 0}</div>
          <div className="summary-label">Scheduled</div>
        </div>
      </div>
      <div className="summary-card">
        <span className="summary-icon">
          <TagIcon />
        </span>
        <div>
          <div className="summary-number">{topicCount}</div>
          <div className="summary-label">Active topics</div>
        </div>
      </div>
    </div>
  );

  const renderArticleCard = (post: BlogPostDto) => {
    const imageUrl = postImageUrl(post);
    const tone = categoryImageClass(post.category);
    const info = statusInfo(post, statusFilter);
    const tags = (post.tags || []).slice(0, 3);
    const isBusy = busySlug === post.slug;
    const isPublicPublished = String(post.status).toUpperCase() === 'PUBLISHED' && post.visibility === 'public';

    return (
      <article key={post.id} className="article-card">
        <div className={`article-image ${tone}`}>
          {imageUrl && <img src={imageUrl} alt="" />}
          <span className="image-label">
            <CategoryIcon category={post.category} />
            {categoryLabel(post.category)}
          </span>
        </div>

        <div className="article-content">
          <div className="article-topline">
            <div className="article-meta">
              <span>Updated {formatDate(post.updatedAt)}</span>
              <span className="dot" />
              <span>{post.readTimeMinutes ?? 1} min read</span>
              <span className="dot" />
              <span>{post.authorDisplayName || 'AACore Team'}</span>
            </div>
            <div className="card-actions" data-open={openMenuSlug === post.slug ? 'true' : 'false'}>
              <button
                type="button"
                className="menu-button"
                aria-label="Article actions"
                aria-haspopup="menu"
                aria-expanded={openMenuSlug === post.slug}
                disabled={isBusy}
                onClick={() => setOpenMenuSlug((s) => (s === post.slug ? null : post.slug))}
              >
                <KebabIcon />
              </button>
              <div className="action-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => void openPreview(post)}>
                  <EyeIcon /> Preview
                </button>
                <Link role="menuitem" to={`/blog/edit/${post.slug}`} onClick={() => setOpenMenuSlug(null)}>
                  <EditIcon /> Edit
                </Link>
                <button type="button" role="menuitem" onClick={() => void handleDuplicate(post)}>
                  <CopyIcon /> Duplicate
                </button>
                <button type="button" role="menuitem" className="danger-link" onClick={() => void handleDelete(post)}>
                  <TrashIcon /> Delete
                </button>
              </div>
            </div>
          </div>

          <h3 className="article-title">
            <Link to={`/blog/${post.slug}/`}>{post.title}</Link>
          </h3>
          <p className="article-excerpt">{post.summary}</p>

          <div className="tag-wrap">
            {tags.map((tag) => (
              <span key={`${post.id}-${tag.label}`} className="tag">
                {tag.label}
              </span>
            ))}
            <span className={`status-pill ${info.key}`}>{info.label}</span>
            {post.featured && <span className="feature-pill">Featured</span>}
            {isPublicPublished && (
              <a className="tag" href={`/blog/${post.slug}/`} target="_blank" rel="noopener noreferrer">
                View live
              </a>
            )}
          </div>

          <div className="article-bottom">
            <span className="slug-line">/blog/{post.slug}/</span>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f8fc]">
      <CustomerNavbar
        onLogout={handleLogout}
        showDashboardLink
        showLogoutButton
        showAuthButtons
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />

      <div className="aacore-blog-admin">
        <section className="admin-hero">
          <div className="wrap admin-hero-grid">
            <div>
              <p className="eyebrow">Blog administration</p>
              <h1>Manage AACore Booking articles</h1>
              <p className="admin-lead">
                Create, review, and publish guidance for service businesses. Track drafts, scheduled updates, and
                published articles from one place.
              </p>
            </div>

            <div className="hero-actions">
              <button type="button" className="button primary" onClick={() => navigate('/blog/new')}>
                <PlusIcon /> New Article
              </button>
            </div>
          </div>
        </section>

        <section className="wrap">{renderSummaryCards()}</section>

        <section className="main-area">
          <div className="wrap">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h2 className="panel-title">Articles</h2>
                  <p className="panel-text">Search, filter, and manage every article across all statuses and topics.</p>
                </div>
              </div>

              {loadError && (
                <div className="empty-state" data-show="true" style={{ marginBottom: 15 }}>
                  {loadError}
                </div>
              )}

              <details className="filter-block" open>
                <summary>
                  <span>
                    <FilterIcon /> Filters
                  </span>
                  <ChevronDownIcon />
                </summary>
                <div className="toolbar">
                  <label className="field-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      className="field"
                      type="search"
                      placeholder="Search title, summary, tags…"
                      aria-label="Search articles"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </label>
                  <select
                    className="control"
                    aria-label="Filter by status"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as StatusFilterId);
                      setPage(1);
                    }}
                  >
                    {STATUS_FILTERS.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="control"
                    aria-label="Filter by topic"
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value as BlogCategoryId | 'all');
                      setPage(1);
                    }}
                  >
                    {BLOG_CATEGORY_FILTERS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.id === 'all' ? 'All topics' : c.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="control"
                    aria-label="Sort articles"
                    value={sortMode}
                    onChange={(e) => {
                      setSortMode(e.target.value as SortModeId);
                      setPage(1);
                    }}
                  >
                    {SORT_OPTIONS.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="control"
                    aria-label="Articles per page"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n} / page
                      </option>
                    ))}
                  </select>
                </div>
              </details>

              {posts.length > 0 ? (
                <div className="article-list" aria-busy={loading}>
                  {posts.map(renderArticleCard)}
                </div>
              ) : (
                !loadError && (
                  <div className="empty-state" data-show="true">
                    <strong>No articles matched your filters.</strong>
                    <div>Try a broader search, a different topic, or clear the status filter.</div>
                  </div>
                )
              )}

              <div className="pagination">
                <span>
                  {pagination.total === 0
                    ? 'No articles to show'
                    : `Showing ${rangeStart}–${rangeEnd} of ${pagination.total} ${pagination.total === 1 ? 'article' : 'articles'}`}
                </span>
                {pagination.totalPages > 1 && (
                  <nav className="pages" aria-label="Article pagination">
                    <button
                      type="button"
                      className="page-button"
                      disabled={!pagination.hasPrevPage || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    {pageNumbers.map((p, idx) =>
                      p === 'ellipsis' ? (
                        <span key={`ellipsis-${idx}`} className="page-button" style={{ border: 'none', background: 'transparent' }}>
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          className="page-button"
                          aria-current={pagination.page === p ? 'page' : undefined}
                          disabled={loading}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className="page-button"
                      disabled={!pagination.hasNextPage || loading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </nav>
                )}
              </div>
            </div>
          </div>
        </section>

        <dialog
          ref={previewDialogRef}
          aria-label="Article preview"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePreview();
          }}
        >
          <div className="dialog-card">
            <div className="dialog-head">
              <div className="dialog-title-wrap">
                <span className="dialog-title-icon">
                  <EyeIcon />
                </span>
                <div>
                  <h2>{previewPost?.title || 'Article preview'}</h2>
                  <p>Review how this article looks before publishing changes.</p>
                </div>
              </div>
              <button type="button" className="close-button" aria-label="Close preview" onClick={closePreview}>
                <CloseIcon />
              </button>
            </div>

            <div style={{ padding: 21, overflow: 'auto' }}>
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : previewError ? (
                <div className="empty-state" data-show="true">
                  {previewError}
                </div>
              ) : previewPost ? (
                <div className="preview-layout">
                  <article className="preview-article">
                    <span className="tag">{categoryLabel(previewPost.category)}</span>
                    <h2>{previewPost.title}</h2>
                    <p className="preview-summary">{previewPost.summary}</p>
                    {postImageUrl(previewPost) && (
                      <div
                        className="preview-hero"
                        style={{
                          backgroundImage: cssUrl(postImageUrl(previewPost)!),
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    )}
                    <div className="preview-body" dangerouslySetInnerHTML={{ __html: previewBodyHtml || '<p>No content yet.</p>' }} />
                  </article>
                  <aside className="preview-aside">
                    <h3>Publishing details</h3>
                    <div className="preview-details">
                      <div className="preview-detail">
                        <span>Author</span>
                        <strong>{previewPost.authorDisplayName || 'AACore Team'}</strong>
                      </div>
                      <div className="preview-detail">
                        <span>Published</span>
                        <strong>{formatDate(previewPost.publishedAt) || 'Not yet published'}</strong>
                      </div>
                      <div className="preview-detail">
                        <span>Read time</span>
                        <strong>{previewPost.readTimeMinutes ?? 1} min</strong>
                      </div>
                      <div className="preview-detail">
                        <span>Category</span>
                        <strong>{categoryLabel(previewPost.category)}</strong>
                      </div>
                      <div className="preview-detail">
                        <span>Status</span>
                        <strong>{statusInfo(previewPost, statusFilter).label}</strong>
                      </div>
                      <div className="preview-detail">
                        <span>Tags</span>
                        <strong>{(previewPost.tags || []).map((t) => t.label).join(', ') || 'None'}</strong>
                      </div>
                    </div>
                  </aside>
                </div>
              ) : null}
            </div>

            <div className="dialog-foot">
              <span className="dialog-foot-note">
                {previewPost ? `/blog/${previewPost.slug}/` : ''}
              </span>
              <div className="foot-actions">
                {previewPost &&
                  String(previewPost.status).toUpperCase() === 'PUBLISHED' &&
                  previewPost.visibility === 'public' && (
                    <a
                      className="button secondary"
                      href={`/blog/${previewPost.slug}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Public URL
                    </a>
                  )}
                {previewPost && (
                  <Link className="button secondary" to={`/blog/edit/${previewPost.slug}`}>
                    <EditIcon /> Edit
                  </Link>
                )}
                <button type="button" className="button primary" onClick={closePreview}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </dialog>
      </div>

      <Footer />
    </div>
  );
};

export default BlogAdminPage;
